import { gpCommon } from '../app';
import { ContextMenuItem } from '../util/context-menus';
import { installContextMenuGestureGuard } from '../util/context-menu-gesture-guard';
//import { ContextMenuItem, RegularMenuItem, Separator, SubMenuItem } from '../util/context-menus';

export type MenuItemById = { [index: string]: ContextMenuItemForLib; }
export type RegularMenuItemForLib = {
    name: string;
    disabled?: boolean;
    visible?: boolean;
    icon?: string | ((opt: any, $itemElement: JQuery, itemKey: string, item: RegularMenuItemForLib) => string);
    callback: () => boolean | void | undefined;
};

export type ToggleMenuItemForLib = {
    name: string;
    disabled?: boolean;
    visible?: boolean;
    isChecked: () => boolean;
    callback: () => boolean | void | undefined;
};

export type SubMenuItemForLib = {
    name: string;
    items: MenuItemById;
    visible?: boolean;
    callback?: () => boolean | void | undefined;
}
export type SeparatorForLib = string;


export const ContextMenuSetupService_ServiceName = 'gpContextMenuSetupService';
export const gpContextMenuSetupService_ServiceName = ContextMenuSetupService_ServiceName;

export interface ContextMenuInstanceReference {
    showMenu(location: { x: number, y: number }): void;
}

export interface gpContextMenuSetupService {
    setupContextMenu(setupOptions: {
        htmlElem: HTMLElement;
        triggerSelector: string;
        getContextMenuItems: (triggerElement: ng.IAugmentedJQuery, pageLocation: { x: number, y: number }) => ContextMenuItem[];
        onShow?: () => void;
    }): ContextMenuInstanceReference;
}

type ContextMenuItemForLib = RegularMenuItemForLib | SeparatorForLib | SubMenuItemForLib;

// GH #336: jQuery-contextMenu activates items on mouseup, so in Safari the release of
// the right-click (or Ctrl+click) that opened the menu runs whatever item was painted
// under the pointer. Installed here, once per bundle, rather than per menu: the guard
// is a document-level capture listener keyed on the menu's own class, so it also covers
// the two menus that build their config directly instead of going through this service
// (gantt-tasklist.ts and react-resource-list/resource-list.tsx). See the guard module
// for why this is not a button test and not itemClickEvent.
installContextMenuGestureGuard(document);

class ContextMenuSetupService implements gpContextMenuSetupService {
    static ServiceName: string = ContextMenuSetupService_ServiceName;
    static $inject = [];

    constructor(
    ) {
    }

    setupContextMenu(setupOptions: {
        htmlElem: HTMLElement;
        triggerSelector: string;
        getContextMenuItems: (triggerElement: ng.IAugmentedJQuery, pageLocation: { x: number, y: number }) => ContextMenuItem[];
        onShow?: () => void;
    }): ContextMenuInstanceReference {

        const { htmlElem, triggerSelector, getContextMenuItems } = setupOptions;
        const jqAny: any = $(htmlElem);



        // context menu comes from here
        // https://swisnl.github.io/jQuery-contextMenu/index.html
        const options: JQueryContextMenuOptions = {
            selector: triggerSelector,
            className: 'gp-context-menu',
            zIndex: 1000,
            reposition: false, // set to false to force the menu to be rebuild on every click
            autoHide: false,
            callback: function (key, options) {
                //let m = "clicked: " + key + " on " + $(this).text();
                //if (window.console) {
                //    console.log(m);
                //}
                //else {
                //    alert(m);
                //}
            },
            events: {
                show: function (options) {
                    if (setupOptions.onShow) setupOptions.onShow();
                    jqAny.contextMenu(false);
                    options.trigger = 'none';
                },
                hide: () => {
                    htmlElem.focus();
                },
            },
            build: function ($triggerElement, e) {
                // Where the menu was invoked, so entries that open floating
                // UI (e.g. "Ask AI") can anchor at the click point. Falls
                // back to the viewport centre when the menu was opened
                // without a pointer event (e.g. programmatically).
                const pageLocation = {
                    x: (e as any)?.pageX ?? window.innerWidth / 2,
                    y: (e as any)?.pageY ?? window.innerHeight / 2,
                };
                let srcItems = getContextMenuItems($triggerElement, pageLocation);
                let menuItemNumber = 0;

                function getNextId() {
                    ++menuItemNumber;
                    return `${menuItemNumber}`;
                }

                let destItems = convertMenuItemDefinition(srcItems, getNextId);

                return {
                    items: destItems,
                    // we set the trigger to none so that repeated right clicks to not re-show the same menu. We want right clicks to redo selection.
                    trigger: 'none',
                };
            },
        };

        $.contextMenu(options);

        return {
            showMenu: (location) => {
                $(triggerSelector).contextMenu(location)
            }
        }
    }


}

gpCommon.service(ContextMenuSetupService.ServiceName, ContextMenuSetupService);




// Exported for menus that own their own $.contextMenu options (Gantt task
// list, resource strips): the conversion is what turns iconFontAwesome
// into real <i class="fa"> markup — raw ContextMenuItems handed straight
// to the lib silently drop icons, disabled reasons, and toggle rendering.
export function convertMenuItemDefinition(srcItems: ContextMenuItem[], getNextId: () => string): MenuItemById {

    let destItems: MenuItemById = {};

    for (const srcItem of srcItems) {
        let destItemId = `item_${getNextId()}`;

        // if we have a togglging check item, we have to convert it to a regular item with special logic
        if (typeof srcItem === 'object' && 'isChecked' in srcItem) {

            const srcCheckItem = srcItem;

            let itemElement: JQuery | null = null;

            function updateCheckItem() {
                if (itemElement) {
                    if (srcCheckItem.isChecked()) {
                        itemElement.html(`<i class="fa fa-check-square-o"></i> <span>${srcCheckItem.name}`);
                    } else {
                        itemElement.html(`<i class="fa fa-square-o"></i> <span>${srcCheckItem.name}`);
                    }
                }
            }

            const newItem: ContextMenuItemForLib = {
                name: srcItem.name,
                icon: (opt: any, $itemElement: JQuery, itemKey: string, item: RegularMenuItemForLib) => {
                    itemElement = $itemElement;
                    updateCheckItem();
                    return ""; // no special class name required
                },
                callback: () => {
                    srcItem.callback();
                    updateCheckItem();
                    return false;
                },
                visible: srcItem.visible,
            }

            destItems[destItemId] = newItem;
        } else if (typeof srcItem === 'object' && 'items' in srcItem) {

            const newItem: SubMenuItemForLib = {
                name: srcItem.name,
                visible: srcItem.visible,
                items: convertMenuItemDefinition(srcItem.items, getNextId),
            }

            destItems[destItemId] = newItem;
        } else if (typeof srcItem === 'object') {

            // icon: (opt, $itemElement, itemKey, item) => ($itemElement.html('<i class="fa fa-clipboard" aria-hidden="true"></i>'

            const newItem: ContextMenuItemForLib = {
                name: srcItem.name,
                icon: (opt: any, $itemElement: JQuery, itemKey: string, item: RegularMenuItemForLib) => {
                    let html = "";


                    if (srcItem.iconFontAwesome) {
                        html += `<i class="fa ${srcItem.iconFontAwesome}" aria-hidden="true"></i>`;
                    }
                    html += `<span>${srcItem.name}</span>`

                    if (srcItem.shortcutKey) {
                        html += `<span class="context-menu-item-hotkey">${srcItem.shortcutKey}</span>`;
                    }
                    $itemElement.html(html);

                    if (srcItem.disabled && srcItem.disabledReason) {
                        $itemElement.attr("title", srcItem.disabledReason);
                    }
                    return ""; // no special class name required
                },
                callback: srcItem.callback,
                disabled: !!srcItem.disabled,
                visible: srcItem.visible,
            }
            destItems[destItemId] = newItem;
        } else {


            destItems[destItemId] = srcItem;
        }
    }

    return destItems;
}

